import { useEffect, useState } from "react";
import {
  BoideAuthError,
  getBoideToken,
  getMe,
  getMyQuotaStatus,
  getProfile,
  logoutBoide,
  type BoideUser,
  type BoideProfile,
} from "@/lib/boide-api";
import { fromBootstrap } from "@/lib/bootstrap";
import { requestQuickTopup } from "@/lib/quick-topup";
import { signOutFirebase } from "@/lib/firebase";

export type BoideUserWithProfile = BoideUser & { profile?: BoideProfile };

let cached: BoideUserWithProfile | null = null;
let inflight: Promise<BoideUserWithProfile | null> | null = null;
const listeners = new Set<(u: BoideUserWithProfile | null) => void>();

/**
 * Bắn mỗi khi hồ sơ/số dư vừa được đọc lại từ BE.
 *
 * 🔴 Đây là tín hiệu "tiền vừa có thể đã đổi" DÙNG CHUNG cho cả app: mọi luồng
 * tiêu tiền (chat, khai mở lá số, chỉ tay, Bói Live, điểm danh, nạp tiền…) đều đã
 * gọi `refreshBoideUser()` sau khi xong. Thứ KHÔNG suy ra được từ `users.amount`
 * là QUOTA GÓI: hạn mức ngày bị trừ TRƯỚC ví, nên một lượt bói của người có gói
 * làm quota tụt mà `amount` đứng yên — người dùng thấy con số quota chỉ đổi sau
 * khi tải lại trang. `useQuotaStatus` nghe sự kiện này để đọc lại.
 */
export const BOIDE_USER_REFRESHED_EVENT = "boide-user-refreshed";

function emit(u: BoideUserWithProfile | null) {
  cached = u;
  listeners.forEach((fn) => fn(u));
  // Chỉ báo ở phía client; SSR không có `window`.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(BOIDE_USER_REFRESHED_EVENT));
  }
}

/**
 * Ví vừa về 0 CHƯA CHẮC là hết tiền: quota gói bị trừ TRƯỚC ví, nên người đang có
 * gói còn hạn mức ngày vẫn bói được bình thường. Đập popup nạp tiền chỉ vì
 * `users.amount = 0` là dán nhãn "hết tiền" lên mặt người vừa mua gói 500k.
 *
 * Hỏi `/subscriptions/me` để lấy `spendable` — ĐÚNG con số mà cổng chặn ở BE dùng
 * (`wallet_service.spendable_balance`) — rồi mới quyết định. BE cũ chưa trả
 * `spendable` thì `Number(undefined)` là NaN, mọi so sánh đều false → rơi về hành
 * vi cũ (vẫn nhắc). Lỗi mạng thì im lặng: thà bỏ lỡ một lời nhắc còn hơn chặn
 * ngang phiên xem của người vẫn còn hạn mức.
 */
async function warnIfNothingLeftToSpend() {
  try {
    const quota = await getMyQuotaStatus();
    if (Number(quota.spendable) > 0) return;
  } catch {
    return;
  }
  requestQuickTopup("Số dư vừa về 0. Nạp nhanh bằng QR để tiếp tục phiên xem.");
}

async function fetchMe(): Promise<BoideUserWithProfile | null> {
  const token = getBoideToken();
  if (!token) {
    emit(null);
    return null;
  }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const [me, profile] = await Promise.all([
        fromBootstrap("me", getMe),
        fromBootstrap("profile", getProfile).catch(() => undefined),
      ]);
      const merged: BoideUserWithProfile = { ...me, profile };
      const previousBalance = cached?.amount ?? cached?.credits;
      const nextBalance = merged.amount ?? merged.credits;
      emit(merged);
      if (
        typeof previousBalance === "number" &&
        previousBalance > 0 &&
        typeof nextBalance === "number" &&
        nextBalance <= 0
      ) {
        void warnIfNothingLeftToSpend();
      }
      return merged;
    } catch (error) {
      // 🔴 CHỈ đăng xuất khi BE nói token hỏng. 503 lúc nginx quá tải, timeout, rớt
      // mạng… mà cũng logout là mỗi lần backend nghẽn vài giây, ai F5 cũng bị đá ra
      // màn đăng nhập — rồi cùng đăng nhập lại, đổ thêm tải lên đúng lúc đang nghẽn.
      // Lỗi tạm thời thì giữ user đã cache (có thể null nếu chưa từng lấy được).
      if (!(error instanceof BoideAuthError)) {
        console.warn("[useBoideUser] getMe failed, keeping session:", error);
        return cached;
      }
      logoutBoide();
      await signOutFirebase();
      emit(null);
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useBoideUser() {
  const [user, setUser] = useState<BoideUserWithProfile | null>(cached);
  const [ready, setReady] = useState<boolean>(cached !== null || !getBoideToken());

  useEffect(() => {
    listeners.add(setUser);
    if (cached === null && getBoideToken()) {
      fetchMe().finally(() => setReady(true));
    } else {
      setReady(true);
    }
    return () => {
      listeners.delete(setUser);
    };
  }, []);

  return { user, ready, refresh: fetchMe };
}

export function clearBoideUser() {
  emit(null);
}

export function refreshBoideUser() {
  return fetchMe();
}

export function displayName(
  u: BoideUserWithProfile | BoideUser | null | undefined,
  fallback = "Quý Khách",
) {
  if (!u) return fallback;
  const profile = (u as BoideUserWithProfile).profile;
  return profile?.real_name || u.full_name || u.name || profile?.username || u.username || fallback;
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  const last = parts[parts.length - 1] || "";
  const first = parts.length > 1 ? parts[0] : "";
  return ((first[0] || "") + (last[0] || "")).toUpperCase() || "QK";
}

export function isProfileIncomplete(profile?: BoideProfile | null): boolean {
  if (!profile) return false;
  // Giờ sinh KHÔNG bắt buộc (nhiều người không nhớ) — chỉ cần ngày sinh + giới tính.
  return !profile.birth_date || !profile.gender;
}

// Nhắc hoàn thiện hồ sơ tối đa 1 lần mỗi phiên duyệt web: user đóng/bỏ qua dialog
// thì không hiện lại cho tới lần mở web kế tiếp (sessionStorage sống qua reload
// trong cùng tab, mất khi đóng trình duyệt/tab).
const PROFILE_PROMPT_DISMISSED_KEY = "boide_profile_prompt_dismissed";

export function markProfilePromptDismissed() {
  try {
    sessionStorage.setItem(PROFILE_PROMPT_DISMISSED_KEY, "1");
  } catch {
    // sessionStorage có thể bị chặn (private mode) — bỏ qua, chỉ mất tính năng nhắc 1 lần
  }
}

export function isProfilePromptDismissed(): boolean {
  try {
    return sessionStorage.getItem(PROFILE_PROMPT_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function getDefaultDivinerId(profile?: BoideProfile | null): string {
  console.log("[getDefaultDivinerId] profile input:", profile);
  if (!profile) {
    console.log("[getDefaultDivinerId] No profile, default to cu_an");
    return "cu_an";
  }

  if (profile.default_diviner_id) return profile.default_diviner_id;

  const genderStr = String(profile.gender || "")
    .toLowerCase()
    .trim();
  const isFemale = genderStr === "female" || genderStr === "nữ" || genderStr === "nu";
  const isMale = genderStr === "male" || genderStr === "nam";

  // Calculate age
  let age: number | undefined;
  if (profile.birth_date) {
    const birthDate = new Date(profile.birth_date);
    if (!isNaN(birthDate.getTime())) {
      const today = new Date();
      age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
    }
  }

  console.log(
    `[getDefaultDivinerId] genderStr: "${genderStr}", isMale: ${isMale}, isFemale: ${isFemale}, calculated age: ${age}`,
  );

  if (age !== undefined && age < 30) {
    if (isFemale) {
      console.log("[getDefaultDivinerId] Female < 30 -> tu_sinh (Cậu Tú)");
      return "tu_sinh";
    }
    if (isMale) {
      console.log("[getDefaultDivinerId] Male < 30 -> may (Bé Mây)");
      return "may";
    }
  }

  console.log("[getDefaultDivinerId] Default to cu_an (Cụ Ẩn)");
  return "cu_an"; // Còn lại -> Cụ Ẩn
}
